/**
 * SelectionContext - Manages selection state
 * 
 * Handles:
 * - Selected waypoint index
 * - Selected feature index
 * - Multi-selection for batch operations
 * - Via-point selection state
 */
import { createContext, useContext, useState, useCallback } from 'react';

const SelectionContext = createContext(null);

export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within SelectionProvider');
  }
  return context;
};

export const SelectionProvider = ({ children }) => {
  const [selectedWaypointIndex, setSelectedWaypointIndex] = useState(null);
  const [selectedIsViaPoint, setSelectedIsViaPoint] = useState(false);
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(null);
  const [multiSelectedIndices, setMultiSelectedIndices] = useState(new Set());

  const toggleMultiSelect = useCallback((index) => {
    setMultiSelectedIndices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const clearMultiSelect = useCallback(() => {
    setMultiSelectedIndices(new Set());
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedWaypointIndex(null);
    setSelectedIsViaPoint(false);
    setSelectedFeatureIndex(null);
    setMultiSelectedIndices(new Set());
  }, []);

  const value = {
    selectedWaypointIndex,
    setSelectedWaypointIndex,
    selectedIsViaPoint,
    setSelectedIsViaPoint,
    selectedFeatureIndex,
    setSelectedFeatureIndex,
    multiSelectedIndices,
    toggleMultiSelect,
    clearMultiSelect,
    clearSelection,
  };

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
};
